// Module-scoped Strudel engine lifecycle.
//
// @strudel/web is a singleton — one AudioContext, one repl, created
// once and reused for the life of the page. We don't try to tear it
// down: repeated initStrudel() calls return the same repl, and
// closing its context breaks it permanently.
//
// Stop behavior: hush() the active pattern + stop() the cyclist.
// Already-playing Web Audio nodes finish their release + any reverb
// / delay tails naturally. That's Strudel's model. Several attempts
// at a "kill switch" (suspend ctx, close ctx, swap a master gain via
// a destination getter, mute a master gain) all broke audio outright
// against Strudel's singleton + cached-master-output internals. The
// fix for "tails too long on stop" lives in the kits — keep release
// and reverb modest so Stop reads as Stop, not slow-fade.

import {
  openSampleStorage,
  type SampleStorage,
} from '../samples/storage/idb';
import { rehydrateLocalSamples } from '../samples/rehydrate';
import { ensureMasterAnalyser, getMasterAnalyser } from './masterAnalyser';
import { registerKnobSignal } from './knobSignal';
import type { Repl, StatusHandler, StrudelModule } from './types';

const STRUDEL_URL = 'https://esm.sh/@strudel/web';
const SOUNDFONTS_URL = 'https://esm.sh/@strudel/soundfonts';
const DIRT_SAMPLES = 'github:tidalcycles/dirt-samples';

let replPromise: Promise<Repl> | null = null;
let repl: Repl | null = null;
// The imported @strudel/web module. Retained so extensions.ts can pull
// registerSound / samples / registerWorklet off it without re-importing
// and without relying solely on globalThis probing.
let strudelModule: StrudelModule | null = null;
// hush(): replaces the current pattern with silence. Called from
// stop() so the scheduler doesn't push one more set of haps into
// its lookahead window on the way down.
let hush: (() => void) | null = null;
// Captured after init for the Settings modal's diagnostics view.
// Read-only — we never mutate the context or its destination.
let audioContext: AudioContext | null = null;

// Recording tap. Every `.connect(destination)` call is forked to this
// node so a MediaRecorder reading from `recordingTap.stream` hears
// exactly what the speakers hear. Lazily created once the audio
// context exists; the connect() patch closes over this variable and
// starts forking the moment it's non-null.
let recordingTap: MediaStreamAudioDestinationNode | null = null;
let connectPatchInstalled = false;

// Shared IDB-backed storage for user samples + packs. Opened once after
// the engine boots; every consumer (forge, drawer, rehydrate) reads from
// this single instance so dedup + transactions behave correctly.
let sampleStorage: SampleStorage | null = null;

export function initPlayer(onStatus?: StatusHandler): Promise<Repl> {
  if (replPromise) return replPromise;

  replPromise = (async () => {
    onStatus?.('Importing @strudel/web…');

    let mod: StrudelModule;
    try {
      mod = (await import(/* @vite-ignore */ STRUDEL_URL)) as StrudelModule;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      throw new Error('Failed to import @strudel/web: ' + msg);
    }

    if (typeof mod.initStrudel !== 'function') {
      throw new Error('initStrudel export missing from @strudel/web');
    }

    strudelModule = mod;

    // Install the recording tap patch before initStrudel so that every
    // subsequent connect(destination) from superdough's voice chains
    // also reaches our MediaStream tap. The tap node itself doesn't
    // exist yet — the patch late-binds it.
    installConnectPatch();

    onStatus?.('Booting audio engine…');
    repl = await mod.initStrudel();

    const maybeHush = (mod as unknown as { hush?: () => void }).hush;
    if (typeof maybeHush === 'function') hush = maybeHush;

    captureAudioContext(mod);
    createRecordingTap();
    if (audioContext) ensureMasterAnalyser(audioContext);
    registerKnobSignal();

    // Dirt samples + soundfonts block on init so early clicks don't
    // rain "sound not found" errors. Each loader is no-throw; boot
    // always resolves.
    onStatus?.('Loading samples + soundfonts…');
    await Promise.all([loadDirtSamples(onStatus), loadSoundfonts(onStatus)]);

    // Rehydrate user samples last: it depends on registerSound being
    // installed (superdough ready) and on the AudioContext being
    // captured (we decode into it). Failures are logged via onStatus
    // but never block Ready — the engine is still fully usable.
    await rehydrateUserSamples(onStatus);

    onStatus?.('Ready. Click Play.', 'ok');

    return repl;
  })();

  replPromise.catch(() => {
    replPromise = null;
  });

  return replPromise;
}

function captureAudioContext(mod: StrudelModule): void {
  const getCtx = (mod as unknown as { getAudioContext?: () => AudioContext })
    .getAudioContext;
  if (typeof getCtx !== 'function') return;
  try {
    audioContext = getCtx();
  } catch {
    audioContext = null;
  }
}

// Patches AudioNode.prototype.connect to fork every connection whose
// target is an AudioDestinationNode — the patch calls the original
// connect first (preserving audio-to-speakers), then ALSO connects the
// same source to our recording tap when it exists. That way the tap
// hears the final summed output even though Strudel never references
// it directly. instanceof is used rather than reference equality so
// the patch can install before audioContext is captured.
function installConnectPatch(): void {
  if (connectPatchInstalled) return;
  if (typeof AudioNode === 'undefined') return;
  connectPatchInstalled = true;

  const original = AudioNode.prototype.connect;
  function patched(
    this: AudioNode,
    ...args: Parameters<AudioNode['connect']>
  ): ReturnType<AudioNode['connect']> {
    const result = (original as Function).apply(this, args) as ReturnType<
      AudioNode['connect']
    >;
    const target = args[0];
    if (
      typeof AudioDestinationNode !== 'undefined' &&
      target instanceof AudioDestinationNode
    ) {
      if (recordingTap) {
        try {
          (original as Function).call(this, recordingTap);
        } catch {
          // A node can refuse a second connection (e.g., already-closed
          // context). Swallow — speakers still work; recording gracefully
          // degrades to silence on that one edge.
        }
      }
      const analyser = getMasterAnalyser();
      if (analyser) {
        try {
          (original as Function).call(this, analyser);
        } catch {
          // Same tolerance as recording: speakers + recording are fine,
          // the spectrum just goes flat for this voice.
        }
      }
    }
    return result;
  }
  AudioNode.prototype.connect = patched as AudioNode['connect'];
}

function createRecordingTap(): void {
  if (!audioContext || recordingTap) return;
  try {
    recordingTap = audioContext.createMediaStreamDestination();
  } catch {
    recordingTap = null;
  }
}

/** MediaStream carrying the final mixed audio output. Null until the
 *  engine has booted. The caller (recorder) pipes this into a
 *  MediaRecorder to produce a blob. */
export function getRecordingStream(): MediaStream | null {
  return recordingTap?.stream ?? null;
}

/** For smoke-tests and Settings diagnostics — true once the tap node
 *  has been created and any nodes connecting to the destination will
 *  be forked into the MediaStream. */
export function hasRecordingTap(): boolean {
  return recordingTap !== null;
}

/** The shared AudioContext owned by the Strudel singleton. Returned
 *  for consumers that need to decode captured audio (recorder →
 *  WAV pipeline). Never mutate or close it — see the comment at the
 *  top of this file. */
export function getAudioContext(): AudioContext | null {
  return audioContext;
}

export function play(code: string): void {
  if (!repl) throw new Error('Audio engine not initialized yet');
  void repl.evaluate(code);
}

// Stop + reset cycle to 0 + re-evaluate. Distinct from play() because
// play() over a still-running scheduler hot-swaps the pattern but
// keeps the cyclist's accumulated position, so arrange() / firstOf()
// resume mid-song. This is the only way to hear a song from cycle 0
// again after it has been playing.
export function restart(code: string): void {
  if (!repl) throw new Error('Audio engine not initialized yet');
  try {
    hush?.();
  } catch {
    // Hush should never throw; future-proof guard.
  }
  repl.stop();
  repl.scheduler.setCycle?.(0);
  void repl.evaluate(code);
}

export function stop(): void {
  if (!repl) return;
  try {
    hush?.();
  } catch {
    // Hush should never throw; guard so a future Strudel version
    // swapping the export surface doesn't break stop.
  }
  repl.stop();
}

export function pause(): void {
  if (!repl) return;
  repl.pause();
}

// Resume from a paused state without re-evaluating the current code.
// Distinct from play(): play re-evaluates and restarts from cycle 0.
export function resume(): void {
  if (!repl) return;
  repl.start();
}

export function getRepl(): Repl | null {
  return repl;
}

/** The captured @strudel/web module, or null before boot completes.
 *  Consumed by extensions.ts to reach registerSound / samples /
 *  registerWorklet without re-importing. Not for app code. */
export function getStrudelModule(): StrudelModule | null {
  return strudelModule;
}

/** Snapshot of the engine's internal state for the Settings modal's
 *  diagnostics view. Purely informational — called synchronously on
 *  render, so it does no work and touches no refs the ticker updates. */
export type EngineDiagnostics = {
  hasRepl: boolean;
  hasAudioContext: boolean;
  audioContextState: 'running' | 'suspended' | 'closed' | 'unknown' | null;
  sampleRate: number | null;
  hasMasterGain: boolean;
  hasHush: boolean;
  hasRecordingTap: boolean;
};

export function getEngineDiagnostics(): EngineDiagnostics {
  const state = audioContext?.state ?? null;
  // The Web Audio spec locks state to a known set, but the type is
  // widened to string in some TS lib versions — normalise defensively.
  const normalizedState =
    state === 'running' ||
    state === 'suspended' ||
    state === 'closed'
      ? state
      : state
        ? 'unknown'
        : null;
  return {
    hasRepl: repl !== null,
    hasAudioContext: audioContext !== null,
    audioContextState: normalizedState,
    sampleRate: audioContext?.sampleRate ?? null,
    hasMasterGain: false,
    hasHush: hush !== null,
    hasRecordingTap: recordingTap !== null,
  };
}

// Dirt samples are needed for s("bd"), s("hh"), s("ho"), s("cp") —
// essentially every drum voice in the catalog. The github URL loads
// a manifest; individual audio blobs stream on demand.
function loadDirtSamples(onStatus?: StatusHandler): Promise<void> {
  const samples = (globalThis as { samples?: (url: string) => unknown }).samples;
  if (typeof samples !== 'function') return Promise.resolve();
  return Promise.resolve(samples(DIRT_SAMPLES))
    .then(() => {
      onStatus?.('Dirt samples indexed.', 'ok');
    })
    .catch((e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      onStatus?.('Samples failed (synths still work): ' + msg, 'warn');
    });
}

// GM soundfonts — needed for s("gm_acoustic_grand_piano"), s("gm_celesta"),
// s("gm_epiano1"), etc. registerSoundfonts() installs a sound-handler
// that lazy-loads individual font blobs on first pattern hit.
type SoundfontsModule = {
  registerSoundfonts?: () => void | Promise<void>;
};

// Open the shared IDB storage and register every persisted sample with
// the engine. Tolerant of IDB being unavailable (e.g. private mode in
// some browsers) — the feature just degrades to in-memory-only.
async function rehydrateUserSamples(onStatus?: StatusHandler): Promise<void> {
  if (!audioContext) return;
  try {
    sampleStorage = await openSampleStorage();
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    onStatus?.('Sample library unavailable: ' + msg, 'warn');
    return;
  }
  try {
    const { registered, failed } = await rehydrateLocalSamples(
      audioContext,
      sampleStorage,
    );
    if (registered === 0 && failed === 0) return;
    const tag = failed === 0 ? 'ok' : 'warn';
    onStatus?.(
      `User samples: ${registered} ready${failed ? `, ${failed} skipped` : ''}.`,
      tag,
    );
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    onStatus?.('Sample rehydrate failed: ' + msg, 'warn');
  }
}

/** The shared SampleStorage instance, or null before boot completes.
 *  Consumed by the forge's commit pipeline and the drawer's list view. */
export function getSampleStorage(): SampleStorage | null {
  return sampleStorage;
}

async function loadSoundfonts(onStatus?: StatusHandler): Promise<void> {
  try {
    const sf = (await import(
      /* @vite-ignore */ SOUNDFONTS_URL
    )) as SoundfontsModule;
    if (typeof sf.registerSoundfonts !== 'function') {
      onStatus?.(
        'Soundfonts skipped: registerSoundfonts export missing.',
        'warn',
      );
      return;
    }
    await sf.registerSoundfonts();
    onStatus?.('GM soundfonts ready (piano, bell, strings).', 'ok');
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    onStatus?.('Soundfonts failed (wave synths still work): ' + msg, 'warn');
  }
}
